import { useCallback, useEffect, useRef, useState } from 'react';
import { learningHubService, billingService } from '../services/api';
import { normalizeLanguageCode } from '../utils/languagePairPolicy';

// Kept for compatibility with the existing home, cohort, and entitlement
// listeners. A program switch is a broader learning-context change, so those
// surfaces should refresh through the same event instead of learning about a
// second event name.
const CHANGE_EVENT = 'learningContextChanged';

const asArray = (value) => (Array.isArray(value) ? value : []);

function programIdFor(program, index = 0) {
  return String(
    program?.programId
    || program?.id
    || program?._id
    || program?.key
    || `program-${index}`,
  );
}

function normalizeGrant(grant, index = 0) {
  if (!grant || typeof grant !== 'object') return null;
  const organization = grant.organization && typeof grant.organization === 'object'
    ? grant.organization
    : null;
  const organizationId = String(
    grant.organizationId
    || organization?.organizationId
    || organization?.id
    || organization?._id
    || '',
  );
  return {
    ...grant,
    grantId: String(grant.grantId || grant.id || grant._id || `${organizationId || 'grant'}-${index}`),
    organizationId,
    organizationName: grant.organizationName || organization?.name || grant.sponsorName || '',
    status: grant.status || grant.state || '',
    effectiveTier: grant.effectiveTier || grant.tier || '',
    expiresAt: grant.expiresAt || grant.seatExpiresAt || null,
  };
}

function grantsFor(program) {
  const raw = program?.access?.grants
    || program?.accessGrants
    || program?.sponsorships
    || program?.sponsors
    || [];
  return asArray(raw).map(normalizeGrant).filter(Boolean);
}

function normalizeLevel(level) {
  if (level && typeof level === 'object') {
    return level.cefr || level.value || level.code || '';
  }
  return typeof level === 'string' || typeof level === 'number' ? String(level) : '';
}

function normalizeProgram(program, index = 0) {
  const programId = programIdFor(program, index);
  const organization = program?.organization && typeof program.organization === 'object'
    ? program.organization
    : null;
  const cohort = program?.cohort && typeof program.cohort === 'object'
    ? program.cohort
    : null;
  const language = program?.language && typeof program.language === 'object'
    ? program.language
    : {};
  const programType = program?.programType
    || program?.type
    || (program?.contextType === 'institution' ? 'institution_self_paced' : 'personal_self_paced');

  return {
    ...program,
    key: programId,
    programId,
    programType,
    name: program?.name || program?.title || program?.programTitle || '',
    nameKey: program?.nameKey || '',
    organization,
    organizationId: String(
      program?.organizationId
      || organization?.organizationId
      || organization?.id
      || organization?._id
      || '',
    ),
    organizationName: program?.organizationName || organization?.name || '',
    cohort,
    targetLanguage: normalizeLanguageCode(
      language.targetLanguage
      || program?.targetLanguage
      || cohort?.targetLanguage
      || '',
    ),
    nativeLanguage: normalizeLanguageCode(
      language.nativeLanguage
      || program?.nativeLanguage
      || cohort?.nativeLanguage
      || '',
    ),
    level: normalizeLevel(program?.level || cohort?.level),
    community: program?.community || null,
    cohorts: asArray(program?.cohorts),
    access: {
      ...(program?.access || {}),
      available: program?.access?.available !== false,
      grants: grantsFor(program),
    },
    isActive: program?.isActive === true,
    // Legacy switch payloads remain attached to fallback programs. New program
    // records use programId and the dedicated program activation endpoint.
    contextType: program?.contextType || (programType.startsWith('institution_') ? 'institution' : 'personal'),
  };
}

// Defensive grouping: older or transitional APIs may emit one copy of a
// self-paced program per sponsor. The switcher must show one learning program,
// with those sponsors nested under Access details. Program identity is the
// grouping boundary; organization identity is never used as a substitute.
function mergeDuplicatePrograms(programs) {
  const merged = new Map();
  programs.forEach((program) => {
    const existing = merged.get(program.programId);
    if (!existing) {
      merged.set(program.programId, program);
      return;
    }
    const grants = [...asArray(existing.access?.grants), ...asArray(program.access?.grants)];
    const uniqueGrants = [...new Map(grants.map((grant) => [grant.grantId, grant])).values()];
    const organizationGrantCount = new Set(
      uniqueGrants
        .filter((grant) => grant.organizationName)
        .map((grant) => grant.organizationId || grant.organizationName),
    ).size;
    const sponsoredByCount = Math.max(
      Number(existing.access?.sponsoredByCount) || 0,
      Number(program.access?.sponsoredByCount) || 0,
      organizationGrantCount,
    );
    merged.set(program.programId, {
      ...existing,
      ...program,
      name: program.name || existing.name,
      nameKey: program.nameKey || existing.nameKey,
      targetLanguage: program.targetLanguage || existing.targetLanguage,
      nativeLanguage: program.nativeLanguage || existing.nativeLanguage,
      level: program.level || existing.level,
      organization: program.organization || existing.organization,
      organizationId: program.organizationId || existing.organizationId,
      organizationName: program.organizationName || existing.organizationName,
      cohort: program.cohort || existing.cohort,
      community: program.community || existing.community,
      isActive: existing.isActive || program.isActive,
      cohorts: [...asArray(existing.cohorts), ...asArray(program.cohorts)],
      access: { ...existing.access, ...program.access, grants: uniqueGrants, sponsoredByCount },
    });
  });
  return [...merged.values()];
}

function programsFromLegacyContexts(contexts) {
  const list = asArray(contexts);
  const personalContext = list.find((context) => context?.contextType === 'personal') || {};
  const sponsorGrants = list
    .filter((context) => context?.contextType === 'institution' && context?.organizationName)
    .map((context, index) => normalizeGrant({
      grantId: `legacy:${context.organizationId || index}`,
      organizationId: context.organizationId,
      organizationName: context.organizationName,
      status: context.selfPaced?.seatStatus,
      effectiveTier: context.tier,
      expiresAt: context.selfPaced?.seatExpiresAt,
    }))
    .filter(Boolean);
  const personalProgram = normalizeProgram({
    ...personalContext,
    programId: 'personal:self-paced',
    programType: 'personal_self_paced',
    name: '',
    cohorts: [],
    contextType: 'personal',
    organizationId: '',
    access: {
      available: true,
      effectiveTier: personalContext.tier || '',
      sponsoredByCount: sponsorGrants.length,
      grants: sponsorGrants,
    },
  });

  const cohortPrograms = list.flatMap((context) => asArray(context?.cohorts).map((cohort, index) => {
    const institution = context?.contextType === 'institution';
    const cohortId = String(cohort?.id || cohort?._id || cohort?.cohortId || `${context?.key || 'cohort'}-${index}`);
    return normalizeProgram({
      programId: `cohort:${cohortId}`,
      programType: institution ? 'institution_cohort' : 'public_cohort',
      name: cohort?.title || cohort?.programTitle || '',
      organizationId: institution ? context.organizationId : '',
      organizationName: institution ? context.organizationName : '',
      cohort: { ...cohort, id: cohortId },
      contextType: institution ? 'institution' : 'personal',
      targetLanguage: cohort?.targetLanguage || '',
      access: { available: cohort?.accessExpired !== true, grants: [] },
    }, index);
  }));

  return [personalProgram, ...cohortPrograms];
}

export function normalizeLearningPrograms(data = {}) {
  const hasProgramEnvelope = Array.isArray(data.programs);
  const rawPrograms = hasProgramEnvelope
    ? data.programs
    : programsFromLegacyContexts(data.contexts);
  const programs = mergeDuplicatePrograms(rawPrograms.map(normalizeProgram));
  const activeId = String(
    data.activeProgramId
    || data.activeProgram?.programId
    || data.activeProgram?.id
    || data.activeProgram?.key
    || '',
  );
  let activeProgram = programs.find((program) => (
    (activeId && program.programId === activeId)
    || (!activeId && program.isActive)
  ));

  if (!activeProgram) activeProgram = programs[0] || null;

  return {
    programs,
    activeProgram,
    activeProgramId: activeProgram?.programId || activeId || '',
    contexts: asArray(data.contexts),
    activeContext: data.activeContext || null,
    usesProgramApi: hasProgramEnvelope,
    fallbackApplied: data.fallbackApplied === true,
    // Whether self-paced study needs a plan this learner does not have.
    selfPacedAccess: data.selfPacedAccess || null,
  };
}

function applyProgramLanguage(program) {
  if (!program) return;
  const targetLanguage = normalizeLanguageCode(program.targetLanguage || program.language?.targetLanguage || '');
  const nativeLanguage = normalizeLanguageCode(program.nativeLanguage || program.language?.nativeLanguage || '');
  let pairChanged = false;
  if (targetLanguage && localStorage.getItem('targetLanguage') !== targetLanguage) {
    localStorage.setItem('targetLanguage', targetLanguage);
    pairChanged = true;
  }
  if (nativeLanguage && localStorage.getItem('nativeLanguage') !== nativeLanguage) {
    localStorage.setItem('nativeLanguage', nativeLanguage);
    pairChanged = true;
  }
  if (pairChanged) {
    localStorage.setItem('languagePairConfirmed', 'true');
    window.dispatchEvent(new CustomEvent('targetLanguageChanged', {
      detail: { targetLanguage, nativeLanguage, source: 'learningProgram' },
    }));
  }
}

export function emitLearningContextChanged(detail = {}) {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
}

export function useLearningContext() {
  const requestGenerationRef = useRef(0);
  const [state, setState] = useState({
    contexts: [],
    activeContext: null,
    programs: [],
    activeProgram: null,
    activeProgramId: '',
    usesProgramApi: false,
    fallbackApplied: false,
    selfPacedAccess: null,
    loading: true,
    error: false,
  });

  const load = useCallback(async () => {
    const requestGeneration = ++requestGenerationRef.current;
    try {
      // The program endpoint is introduced alongside the new backend model.
      // Feature detection lets this UI ship safely before/after the dirty API
      // service file receives its two small method bindings.
      const getPrograms = learningHubService.getPrograms;
      let res;
      if (typeof getPrograms === 'function') {
        const programResponse = await getPrograms();
        res = Array.isArray(programResponse?.data?.programs)
          ? programResponse
          : await learningHubService.getContexts();
      } else {
        res = await learningHubService.getContexts();
      }
      const responseData = res?.data || {};
      const normalized = normalizeLearningPrograms({
        ...responseData,
        activeProgramId: Array.isArray(responseData.programs)
          ? responseData.activeProgramId
          : localStorage.getItem('activeLearningProgramId') || responseData.activeProgramId,
      });
      if (requestGeneration !== requestGenerationRef.current) return null;
      if (normalized.activeProgramId) {
        localStorage.setItem('activeLearningProgramId', normalized.activeProgramId);
      }
      applyProgramLanguage(normalized.activeProgram);
      setState({ ...normalized, loading: false, error: false });
      return normalized;
    } catch (_) {
      if (requestGeneration !== requestGenerationRef.current) return null;
      // Guests / logged-out users get 401 here. Treat it as no programs so the
      // authenticated-only selector simply stays out of the interface.
      setState((current) => ({
        ...current,
        contexts: [],
        activeContext: null,
        programs: [],
        activeProgram: null,
        activeProgramId: '',
        loading: false,
        error: true,
      }));
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      if (cancelled) return;
      setState((current) => ({ ...current, loading: true }));
      load();
    };
    load();
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => {
      cancelled = true;
      requestGenerationRef.current += 1;
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, [load]);

  const switchProgram = useCallback(async (program) => {
    // Any request started before this activation is stale by definition. A
    // later load receives a new generation and is the only response allowed to
    // update the selected program.
    requestGenerationRef.current += 1;
    let res;
    if (state.usesProgramApi && typeof learningHubService.switchProgram === 'function') {
      res = await learningHubService.switchProgram(program.programId);
    } else {
      res = await billingService.switchSubscriptionContext({
        contextType: program.contextType,
        organizationId: program.organizationId || '',
      });
    }

    // The legacy context switch answers with entitlements; the program switch
    // answers with the program list only, so re-read the account (as mobile
    // does) or a cohort with a different plan keeps the old plan's limits.
    let entitlements = res?.data?.entitlements;
    if (!entitlements) {
      try {
        entitlements = (await billingService.getAccount())?.data?.entitlements;
      } catch (_) { /* The program switch stands; limits refresh on the next account read. */ }
    }
    if (entitlements) {
      localStorage.setItem('aiEntitlements', JSON.stringify(entitlements));
      localStorage.setItem('subscriptionTier', entitlements.subscriptionTier || 'plus');
    }

    const responsePrograms = normalizeLearningPrograms(res?.data || {});
    const selectedProgram = state.usesProgramApi
      ? (responsePrograms.activeProgram
        || state.programs.find((item) => item.programId === program.programId)
        || program)
      : (state.programs.find((item) => item.programId === program.programId) || program);
    localStorage.setItem('activeLearningProgramId', selectedProgram.programId);
    applyProgramLanguage(selectedProgram);
    emitLearningContextChanged({
      programId: selectedProgram.programId,
      programType: selectedProgram.programType,
    });
    return res;
  }, [state.programs, state.usesProgramApi]);

  // Older callers use `switchContext`; keep it as an alias while all new UI
  // speaks in terms of programs.
  return { ...state, switchProgram, switchContext: switchProgram, refresh: load };
}
